import { Position } from '@xyflow/react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router';
import { XMarkIcon } from '@heroicons/react/24/outline';
import InputOutputPorts from '@/components/nodes/InputOutputPorts';
import { memo } from 'react';
import { pathwayActions } from '@/stores/pathwayStore';
import { useCaseId } from '@/stores/caseStore';
import { settingsActions } from '@/stores/settingsStore';
import featureConfig from '@/featureConfig';

/**
 * Recycle Icon Component
 * Black Universal Recycling Symbol
 */
const RecycleIcon = () => {
  return (
    <span role="img" aria-label="recycle symbol" className="text-black text-4xl">
      ♺
    </span>
  );
};

/**
 * BalancerNode Component
 * Renders a compact version of PathwayNode for Stream Balancer with a recycle symbol.
 * 
 * - Uses PathwayNode design but shorter/narrower
 * - Recycle symbol centered in the header area
 */
const BalancerNode = ({ data: node }) => {
  const { id, inputs, outputs, label, mirrored } = node;
  const inputPosition = mirrored ? Position.Right : Position.Left;
  const outputPosition = mirrored ? Position.Left : Position.Right;
  const { projectId, pathwayId, nodeId } = useParams();
  const caseId = useCaseId();
  const navigate = useNavigate();
  const removeNode = e => {
    e.stopPropagation();
    pathwayActions.removeNode(id);
    if (nodeId === id) {
      navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}`);
    }
  };

  const handleNodeClick = () => {
    settingsActions.closePalette();
    navigate(`/projects/${projectId}/build/pathways/${pathwayId}/${caseId}/nodes/${id}`, { state: { node } });
  };

  return (
    <div className="relative">
      {/* Label - Floating above the node */}
      <div 
        className="absolute -top-7 left-1/2 transform -translate-x-1/2 flex justify-center pointer-events-none"
      >
        <span className="text-base font-bold text-gray-700 truncate max-w-[300px] text-center bg-white/50 px-1 rounded">
          {label}
        </span>
      </div>

      <div
        className="group/item balancer-node bg-white border border-gray-700 w-96 rounded-lg shadow-lg hover:border-gray-900 relative"
        onClick={handleNodeClick}
      >
        {/* Delete Button - Same style as ArrowNode */}
        {featureConfig.manageModules && (
          <div 
            className="absolute z-20 invisible group-hover/item:visible"
            style={{
               top: -12,
               left: -12
            }}
          >
            <div 
              onClick={removeNode} 
              className="p-1 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-100 text-gray-400 hover:text-red-500 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </div>
          </div>
        )}

        {/* Recycle Icon - Centered in the middle */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
          <RecycleIcon />
        </div>

        {/* Ports - Match PathwayNode styling with spacing adjustments */}
        <div className="relative z-0">
          <div className="flex">
            <div className="flex-1 [&>div]:space-y-3 [&>div>div:last-child]:!mb-0">
              <InputOutputPorts
                parentId={id}
                nodes={mirrored ? outputs : inputs}
                label={mirrored ? 'Outputs' : 'Inputs'}
                handleType={mirrored ? 'output' : 'input'}
                handlePosition={mirrored ? outputPosition : inputPosition}
              />
            </div>
            <div className="flex-1 [&>div]:space-y-3 [&>div>div:last-child]:!mb-0">
              <InputOutputPorts
                parentId={id}
                nodes={mirrored ? inputs : outputs}
                label={mirrored ? 'Inputs' : 'Outputs'}
                handleType={mirrored ? 'input' : 'output'}
                handlePosition={mirrored ? inputPosition : outputPosition}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

BalancerNode.propTypes = {
  data: PropTypes.object,
};

const MemoizedBalancerNode = memo(BalancerNode);

export default MemoizedBalancerNode;
